import { IStoryContent, IStory, IProgressTracker } from '@opengov/stories-shared/types';
import cx from 'classnames2';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TakeScreenshotCallback, useScreenshot } from 'use-react-screenshot';
import { AiApi, AiGeneratedSummaryLength } from '../../../../../../../api/ai-api';
import { IEditorMessages, IUpdateStoryContentProps } from '../../../../../../../types';
import {
  ADD_EMBED,
  ADD_IMAGE,
  ADD_TABLE_TILE,
  ADD_TILE,
  ADD_VIDEO,
  CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE,
  MEDIA_IMAGE,
  MEDIA_TABLE_TILE
} from '../../../../../../constants/modals';
import { getMediaBlockIndexFromId } from '../../../../../../utils/mediaIndex.utils';
import { MediaBlock } from '../../../../MediaBlock';
import { AiSummaryResult, EditorValueSource, IMediaHandlers } from '../../tinyMCEStoryEditorUtils';
import { useToast } from '../../../../../Toast/context/useToast.context';
import { MediaOptionsGearButton, MediaType } from './MediaOptionsGearButton';
import { RemoveSlideButton } from './RemoveSlideButton';
import {
  AiEnabledMediaType,
  HtmlExtractionResult,
  extractIdsForMediaType,
  hasNonHeaderEditable
} from './htmlParsing/htmlParsingUtils';
import html2canvas from 'html2canvas';
import { ProgressTracker } from '../../../../ProgressTracker/ProgressTracker';
import { PROGRESS_TRACKER_MODES } from '../../../../ProgressTracker/constants';
import { extractErrorMsg } from '../../../../../../utils/errors';

// Dictionary for modals
const MODALS = {
  image: ADD_IMAGE,
  video: ADD_VIDEO,
  embed: ADD_EMBED,
  tile: ADD_TILE,
  tableTile: ADD_TABLE_TILE,
  customSpreadsheet: CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE
};

const IMAGE_COMPRESSION_QUALITY = 0.5;

// Poll interval / bound for waiting on window.buildGallery to become available.
// On a cold VIEW load or hard refresh the jQuery + slick CDN scripts injected
// into the iframe may not be ready when the effect first runs, so a single blind
// timeout can miss buildGallery entirely and the gallery never initializes.
const GALLERY_POLL_INTERVAL_MS = 200;
const GALLERY_MAX_POLL_ATTEMPTS = 75; // 75 × 200ms = 15s max before giving up

export interface IDivProps {
  totalReportTables: number;
  readOnly: boolean;
  window: Window;
  storyContent: IStoryContent;
  messages: IEditorMessages;
  mediaHandlers: IMediaHandlers;
  elementProps: {
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    'data-warning'?: string;
    'data-media-type'?: string;
    'data-media-index'?: string;
  };
  children?: React.ReactNode;
  handleRemoveSlide(mediaIndex: number): void;
  handleEditMedia(modal: string, mediaIndex: number): void;
  handleGenerateAiSummary(aiSummary: AiSummaryResult): void;
  contentHtml: string;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  canUseOgAssistGenerateSummary: boolean;
  isAiEnabledByAdmin: boolean;
  editorValueSource: EditorValueSource;
  entityId: number;
  entitySubdomain: string;
  publicationId: string | undefined;
  isCustomSpreadsheetReportEnabled: boolean;
  showProgressTracker: boolean;
  updateStoryContent(storyContent: IStoryContent): void;
  updateStoryContentDB(storyContent: IUpdateStoryContentProps): void;
  story: IStory;
  isTemplate: boolean;
  saveProgressTrackerConfig(props: {
    storyId: string;
    contentId: string;
    config: IProgressTracker['config'];
  }): Promise<void>;
}

export const Div = (props: IDivProps) => {
  const {
    readOnly,
    window,
    elementProps,
    children,
    editorValueSource,
    entityId,
    updateStoryContent,
    updateStoryContentDB,
    storyContent,
    isAiEnabledByAdmin,
    publicationId
  } = props;
  const { toggleToast } = useToast();
  const { className = '' } = elementProps;
  const timeout = useRef<number | null>(null);
  const [galleryWrapper, mediaBlock, slideBlock, hasMedia] = useMemo(
    () => [
      className.indexOf('gallery-wrapper') > -1,
      className.indexOf('media-block') > -1,
      className.indexOf('slide-block') > -1,
      className.indexOf('has-media') > -1
    ],
    [className]
  );
  const [showProgressTracker, setShowProgressTracker] = useState(
    Boolean(Array.isArray(storyContent.contentChartConfig) && storyContent.contentChartConfig.length > 0)
  );
  useEffect(() => {
    if (galleryWrapper) {
      // Poll until buildGallery is in scope (the slick/jQuery CDN scripts may still be
      // loading on a fresh iframe mount), then call it once and stop. buildGallery is
      // idempotent via .not('.slick-initialized'), so a single call after load is enough.
      let attempts = 0;
      timeout.current = window.setInterval(() => {
        if (window.buildGallery) {
          window.buildGallery();
          if (timeout.current) {
            window.clearInterval(timeout.current);
            timeout.current = null;
          }
        } else if (++attempts >= GALLERY_MAX_POLL_ATTEMPTS && timeout.current) {
          window.clearInterval(timeout.current);
          timeout.current = null;
        }
      }, GALLERY_POLL_INTERVAL_MS);
    }
    return () => {
      if (timeout.current) {
        window.clearInterval(timeout.current);
        timeout.current = null;
      }
    };
  }, []);

  const mediaRef = useRef<HTMLDivElement | null>(null);
  const [, takeScreenshot] = useScreenshot();

  const handleProgressTrackerClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowProgressTracker(true);
  }, []);
  if (!readOnly && mediaBlock) {
    const { storyContent, messages, canUseAiToGenerateContent, canUseStoriesSummarizerGA } = props;
    const mediaIndexAttr = elementProps['data-media-index'];
    const mediaIndexFromAttr = mediaIndexAttr ? parseInt(mediaIndexAttr, 10) - 1 : 0;
    // Getting media index based on it's ID is for backward compatibility
    // media blocks divs used to have ids of "media-block-n"
    // We changed ids into "data-media index" attributes
    // If there is an id on the media-block, get index from the id
    const mediaIndex = mediaIndexFromAttr || getMediaBlockIndexFromId(elementProps.id || '') || 0;
    // If block has media, render the media controls
    if (hasMedia) {
      const mediaType = elementProps['data-media-type'] as MediaType;
      const sectionElement = window.document
        .createRange()
        .createContextualFragment(props.contentHtml)
        .querySelector('section');
      if (Array.isArray(storyContent?.contentChartConfig) && storyContent?.contentChartConfig.length > 0) {
        return (
          <ProgressTracker
            messages={messages}
            updateStoryContent={updateStoryContent}
            updateStoryContentDB={updateStoryContentDB}
            story={props.story}
            storyContent={storyContent}
            isTemplate={props.isTemplate}
            initialValue={storyContent.contentChartConfig[0]}
            saveProgressTrackerConfig={props.saveProgressTrackerConfig}
            mode={PROGRESS_TRACKER_MODES.EDIT_WITH_CONFIG}
          />
        );
      }

      const modal = MODALS[mediaType] || null;
      return (
        <div {...elementProps} data-test="media-container">
          <div className="media-children" ref={mediaRef}>
            {children}
          </div>
          <div className={cx('edit-media', { ['edit-embed-media']: mediaType === 'embed' })}>
            {modal ? (
              <MediaOptionsGearButton
                publicationId={publicationId}
                storyContent={storyContent}
                story={props.story}
                isTemplate={props.isTemplate}
                entityId={entityId}
                entitySubdomain={props.entitySubdomain}
                updateStoryContent={updateStoryContent}
                updateStoryContentDB={updateStoryContentDB}
                editorValueSource={editorValueSource}
                canUseAiToGenerateContent={canUseAiToGenerateContent}
                canUseStoriesSummarizerGA={canUseStoriesSummarizerGA}
                canUseOgAssistGenerateSummary={props.canUseOgAssistGenerateSummary}
                isAiEnabledByAdmin={isAiEnabledByAdmin}
                mediaType={mediaType}
                mediaIndex={mediaIndex}
                hasNonHeaderEditable={hasNonHeaderEditable(sectionElement)}
                legacyHandleEditMedia={() => props.handleEditMedia(modal, mediaIndex)}
                messages={props.messages}
                getAiSummaryContext={() => {
                  if (!sectionElement) return null;
                  try {
                    return extractIdsForMediaType({
                      storyContentGroup: storyContent.group,
                      mediaType: mediaType as AiEnabledMediaType,
                      sectionElement,
                      mediaIndexAttr
                    });
                  } catch {
                    // Media that cannot be identified simply withholds the OG Assist entry.
                    return null;
                  }
                }}
                afterGenerateSummary={(extraction, summary) => {
                  props.handleGenerateAiSummary({
                    editorUuid: extraction.editorDataUuid,
                    status: 'complete',
                    value: summary
                  });
                }}
                onGenerateSummary={async (
                  aiEnabledMediaType: AiEnabledMediaType,
                  contentLength: AiGeneratedSummaryLength
                ) => {
                  let extraction: HtmlExtractionResult | null = null;
                  let summary = '';
                  try {
                    extraction = extractIdsForMediaType({
                      storyContentGroup: storyContent.group,
                      mediaType: aiEnabledMediaType,
                      sectionElement,
                      mediaIndexAttr
                    });

                    props.handleGenerateAiSummary({ editorUuid: extraction.editorDataUuid, status: 'generating' });
                    summary = await generateSummary(
                      entityId,
                      extraction,
                      takeScreenshot,
                      mediaRef,
                      contentLength,
                      canUseStoriesSummarizerGA
                    );
                  } catch (error) {
                    console.error(error);
                    toggleToast({
                      title: messages.somethingWentWrongError,
                      body: extractErrorMsg(error, messages.unableToGenerateSummary),
                      severity: 'error'
                    });
                    if (extraction) {
                      props.handleGenerateAiSummary({
                        editorUuid: extraction.editorDataUuid,
                        status: 'error'
                      });
                    }
                  } finally {
                    return { summary, extraction };
                  }
                }}
              />
            ) : null}
            {slideBlock ? (
              <RemoveSlideButton dataHint={messages.deleteSlide} onClick={() => props.handleRemoveSlide(mediaIndex)} />
            ) : null}
          </div>
        </div>
      );
    }

    // Render media selection component by default
    // has-media class is added to the div to have proper styles
    return (
      <div {...elementProps} className={`${elementProps.className} has-media`} data-test="media-selection-container">
        {slideBlock ? (
          <div className="edit-slide delete-slide">
            <RemoveSlideButton dataHint={messages.deleteSlide} onClick={() => props.handleRemoveSlide(mediaIndex)} />
          </div>
        ) : null}
        {showProgressTracker ? (
          <ProgressTracker
            messages={messages}
            updateStoryContent={updateStoryContent}
            updateStoryContentDB={updateStoryContentDB}
            story={props.story}
            storyContent={storyContent}
            isTemplate={props.isTemplate}
            initialValue={storyContent?.contentChartConfig?.[0]}
            saveProgressTrackerConfig={props.saveProgressTrackerConfig}
            mode={PROGRESS_TRACKER_MODES.EDIT_WITH_CONFIG}
          />
        ) : (
          <MediaBlock
            {...props.mediaHandlers}
            totalReportTables={props.totalReportTables}
            messages={messages}
            format={storyContent.group}
            showProgressTracker={props.showProgressTracker}
            handleProgressTracker={handleProgressTrackerClick}
            isTemplate={props.isTemplate}
            isCustomSpreadsheetReportEnabled={props.isCustomSpreadsheetReportEnabled}
            entityId={entityId}
            entitySubdomain={props.entitySubdomain}
            story={props.story}
            publicationId={publicationId}
            storyContent={storyContent}
            updateStoryContent={updateStoryContent}
            updateStoryContentDB={updateStoryContentDB}
          />
        )}
      </div>
    );
  } else if (!readOnly && elementProps['data-warning']) {
    return (
      <div data-test="warning-message-container">
        <div {...elementProps}>{children}</div>
        <div className="warning-message">
          <span className="icon-exclamation-1" />
          <span>{elementProps['data-warning']}</span>
        </div>
      </div>
    );
  } else if (readOnly && elementProps['data-media-type'] === 'progress-tracker') {
    return (
      <ProgressTracker
        messages={props.messages}
        updateStoryContent={updateStoryContent}
        updateStoryContentDB={updateStoryContentDB}
        story={props.story}
        storyContent={storyContent}
        saveProgressTrackerConfig={props.saveProgressTrackerConfig}
        initialValue={storyContent?.contentChartConfig?.[0]}
        mode={PROGRESS_TRACKER_MODES.READ_ONLY}
      />
    );
  }

  return (
    <div {...elementProps} data-test="container">
      {children}
    </div>
  );
};

async function generateSummary(
  entityId: number,
  extraction: HtmlExtractionResult,
  takeScreenshot: TakeScreenshotCallback,
  mediaRef: React.MutableRefObject<HTMLDivElement>,
  contentLength: AiGeneratedSummaryLength,
  useStoriesSummarizerGA: boolean
) {
  const inputImage =
    extraction.mediaType === MEDIA_IMAGE
      ? extraction.imageSrc
      : await getScreenshot(mediaRef, extraction, takeScreenshot);

  const apiMethod = useStoriesSummarizerGA ? AiApi.generateSummary : AiApi.legacyGenerateSummary;

  return extraction.mediaType === MEDIA_IMAGE
    ? await apiMethod({ entityId, inputImage, contentLength })
    : await apiMethod({ entityId, inputImage, embedId: extraction.embedId, contentLength });
}

async function getScreenshot(
  mediaRef: React.MutableRefObject<HTMLDivElement>,
  extraction: HtmlExtractionResult,
  takeScreenshot: TakeScreenshotCallback
) {
  if (extraction.mediaType === MEDIA_IMAGE) {
    return extraction.imageSrc;
  }

  if (!mediaRef.current || extraction.mediaType !== MEDIA_TABLE_TILE) {
    return takeScreenshot(mediaRef.current);
  }
  const tableTitle = mediaRef.current.querySelector('[class*="title"]');
  const tableContainer = mediaRef.current.querySelector('[class*="tableContainer"]');
  const currentTable = tableContainer?.querySelector('table');

  if (!currentTable) {
    return takeScreenshot(mediaRef.current);
  }

  const wideContainer = document.createElement('div');
  try {
    wideContainer.style.position = 'absolute';
    wideContainer.style.left = '-9999px';
    wideContainer.style.top = '0';
    wideContainer.style.width = 'auto';
    wideContainer.style.backgroundColor = 'white';

    const clonedTable = currentTable.cloneNode(true) as HTMLElement;
    clonedTable.style.width = 'auto';
    clonedTable.style.maxWidth = 'none';
    clonedTable.style.tableLayout = 'auto';
    clonedTable.style.margin = '0';
    clonedTable.style.overflow = 'visible';

    const childElements = clonedTable.querySelectorAll('*') as NodeListOf<HTMLElement>;
    childElements.forEach((el) => {
      el.style.border = '0.5px solid black';
      el.style.padding = '4px';
      el.style.overflow = 'visible';
      el.style.maxWidth = 'none';
      el.style.width = 'auto';
    });

    if (tableTitle) {
      const caption = document.createElement('caption');
      caption.textContent = tableTitle.textContent || '';
      caption.style.fontWeight = 'bold';
      caption.style.marginBottom = '8px';
      caption.style.textAlign = 'left';
      clonedTable.insertBefore(caption, clonedTable.firstChild);
    }

    wideContainer.appendChild(clonedTable);
    document.body.appendChild(wideContainer);

    const canvas = await html2canvas(wideContainer, {
      backgroundColor: 'white',
      logging: false,
      scale: 0.75
    });

    return canvas.toDataURL('image/webp', IMAGE_COMPRESSION_QUALITY);
  } catch (error) {
    console.error('Error creating table screenshot:', error);
    return takeScreenshot(mediaRef.current);
  } finally {
    if (document.body.contains(wideContainer)) {
      document.body.removeChild(wideContainer);
    }
  }
}
